import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import { Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Notice from '../Notice/Notice';
import Subtitle from '../Subtitle/Subtitle';
import Button from '../../Buttons/Button/Button';
import BasicPopup from '../../Popups/BasicPopup/BasicPopup';
import styles from './EmployerPersonalInformation.module.css';
import FormField from '../../UpdatedFormUI/FormField/FormField';
import LanguageForm from '../../../Forms/Common/LanguageForm/LanguageForm';
import LanguageLevelList from '../LanguageLevel/LanguageLevelList/LanguageLevelList';
import { EditIcon } from '../../../../assets/images/icons'; // Replace with correct import for the icon
import { updateUser } from '../../../../services/userService';
import { fetchUserByToken, fetchUserByTokenWithoutLoading } from '../../../../app/features/userSlice';

function EmployerPersonalInformation({ employer, }) {
    const user = useSelector((state) => state.user.user);
    const [formLoading, setFormLoading] = useState(false);
    const [isOpenLanguagePopup, setIsOpenLanguagePopup] = useState(false);
    const [editMode, setEditMode] = useState(true);
    const userToken = useSelector((state) => state.user.token);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues: {
            first_name: employer?.first_name || '',
            last_name: employer?.last_name || '',
            email: user.email,
             role: employer?.role || '',
            mobile_number: employer?.mobile_number || '',
            languages: employer.languages || [],

        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email(t('forms.create_account.invalid_email_address'))
                .required(t('general.UI.required')),
               }),
        onSubmit: (values) => {
            if (formLoading) {
                return;
            }
            setFormLoading(true);

            const formData = new FormData();
            formData.append('token', userToken);
            formData.append('first_name', values.first_name);
            formData.append('last_name', values.last_name);
            formData.append('mobile_number', values.mobile_number);
            formData.append('role', values.role);

            if (user.email !== values.email) {
                formData.append('email', values.email);
            }
            if (values?.languages?.length > 0) {
                values?.languages.forEach((language, index) => {
                    formData.append(`languages[${index}][languageCode]`, language.languageCode);
                    formData.append(`languages[${index}][level]`, language.level);
                });
            }
            else {
                formData.append('languages', '');
            }
            // if (values.password) {
            //     formData.append('password', values.password);
            // }
            toast.promise(updateUser(formData), {
                loading: t('edit_profile.updating_profile'),
                success: <b>{t('edit_profile.profile_updated')}</b>,
                error: (err) => <b>{err.response.data.error}</b>,
            }).then(async () => {
                await dispatch(fetchUserByTokenWithoutLoading(userToken));
                const target = document.querySelector('.formScroll');
                if (target) {
                    window.scrollTo({
                        top: target.offsetTop,
                        behavior: 'smooth'
                    });
                }
                if (user) { navigate(`/profile`); }
                await setEditMode(!editMode)
                await setFormLoading(false);
            })
                .catch((error) => {
                    setFormLoading(false);
                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }
                });

        },
    });

    const handlePopupClose = (popupName) => {
        setIsOpenLanguagePopup(false);
    }
    const handlePopupOpen = (popupName) => {
        setIsOpenLanguagePopup(true);
    }
    const handleAddLanguage = (values) => {
        const languageExists = formik?.values?.languages.find((language) => language.languageCode === values.languageCode);
        if (!languageExists) {
            formik.setFieldValue('languages', [...formik.values.languages, { id: Date.now(), ...values }]);
            handlePopupClose('language');
        }
    }
    const handleOptionRemove = (field, id) => {
        const filteredField = formik.values[field].filter((option) => option.id !== id);
        formik.setFieldValue(field, filteredField);
    }

    return (
        <div className={styles.container}>
            <div className={styles.secondColumn}>
                {!editMode && (
                    <Close
                        className={styles.mobileIcon}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            setEditMode(!editMode);
                        }}
                    />
                )}
                <div className={`${styles.title} formScroll`}>{t('personalInformation.title')}</div>
                {editMode ? (
                    <>
                        {employer?.first_name?.length > 0 &&
                            <>
                                <div className={styles.aboutTitle}>{t('employerPersonalInformation.firstName')}</div>
                                <div className={styles.description}>{employer?.first_name}</div>
                            </>
                        }
                        {employer?.last_name?.length > 0 &&
                            <>
                                <div className={styles.aboutTitle}>{t('employerPersonalInformation.LastName')}</div>
                                <div className={styles.description}>{employer?.last_name}</div>
                            </>
                        }
                        <div className={styles.aboutTitle}>{t('forms.login.email')}</div>
                        <div className={styles.description}>{user.email}</div>
                        {employer?.role?.length > 0 &&
                            <>
                                <div className={styles.aboutTitle}>{t('employerPersonalInformation.role')}</div>
                                <div className={styles.description}>{employer?.role}</div>
                            </>
                        }
                        {employer?.mobile_number?.length > 0 &&
                            <>
                                <div className={styles.aboutTitle}>{t('employerPersonalInformation.mobileNumber')}</div>
                                <div className={styles.description}>{employer?.mobile_number}</div>
                            </>
                        }
                        {employer?.languages?.length > 0 &&
                            <>
                                <div className={styles.aboutTitle}>{t('personalInformation.language')}</div>
                                <div className={styles.description}>
                                    {employer?.languages?.map((lang, index) => {
                                        return (
                                            <span key={index}>
                                                {t(`general.language.${lang.languageCode}`)}
                                                {lang.level > 4 && " (" + t('general.fluent') + ")"}
                                                {index < employer?.languages?.length - 1 && ', '}

                                            </span>
                                        )
                                    })}
                                </div>
                            </>}
                    </>
                ) : (
                    <>
                        <div className={styles.editContainer}>
                            <div className={styles.aboutTitle}></div>
                            <FormField
                                name="first_name"
                                label={t('forms.welcome_job_seeker.step_1.first_name')}
                                hasBorder
                                value={formik.values.first_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.first_name && formik.errors.first_name}
                            />
                            <FormField
                                name="last_name"
                                label={t('forms.welcome_job_seeker.step_1.last_name')}
                                hasBorder
                                value={formik.values.last_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.last_name && formik.errors.last_name}
                            />
                            <FormField
                                name="email"
                                type="email"
                                label={t('edit_profile.job_seeker.email')}
                                hasBorder
                                icon={user.email_verified && formik.values.email === user.email && 'verified'}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                                error={formik.touched.email && formik.errors.email}
                            />
                            {!user.email_verified && (
                                <Notice warning>{t('edit_profile.job_seeker.email_notice')}</Notice>
                            )}
                            <FormField
                                name="role"
                                label={t('employerPersonalInformation.role')}
                                hasBorder
                                value={formik.values.role}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.role && formik.errors.role}
                            />
                            <FormField
                                name="mobile_number"
                                label={t('employerPersonalInformation.mobileNumber')}
                                hasBorder
                                value={formik.values.mobile_number}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.mobile_number && formik.errors.mobile_number}
                            />
                           <Subtitle
                                dark
                                hasButton
                                buttonText={t('general.UI.add')}
                                buttonOnClick={() => handlePopupOpen('language')}
                            >
                                {t('general.UI.languages')}
                            </Subtitle>
                            <div style={{ marginBottom: 40 }}>
                                <LanguageLevelList isDark languages={formik.values.languages} onRemove={handleOptionRemove} />
                            </div>
                            <Button onClick={formik.submitForm} className={styles.saveButton}>
                                {formLoading ? t('general.UI.loading') : t('general.UI.save')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
            <div className={styles.thirdColumn}>
                {!editMode ? (
                    <Close
                        className={styles.crossIcon}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            setEditMode(!editMode);
                        }}
                    />
                ) : (
                    <img
                        src={EditIcon}
                        onClick={() => setEditMode(!editMode)}
                        alt={t('personalInformation.edit')}
                        className={styles.editIcon}
                    />
                )}
            </div>
            {isOpenLanguagePopup &&
                <BasicPopup
                    isOpen={isOpenLanguagePopup}
                    closePopup={() => handlePopupClose('language')}
                >
                    <LanguageForm onSubmit={handleAddLanguage} />
                </BasicPopup>
            }
        </div>
    );
}

export default EmployerPersonalInformation;
